import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { LogOut, AlertTriangle, Check, ShieldCheck, Camera, FileText, ChevronRight, X, Clock, Sparkles } from 'lucide-react';
import { FadeIn, FadeInStagger, FadeInStaggerItem } from '../components/ui/AnimatedSection';
import ClientCalendar from '../components/client/ClientCalendar';

export default function Dashboard() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'history' | 'planning' | 'contact' | 'invoices' | 'settings'>('overview');
  const navigate = useNavigate();

  const [upcomingInterventions, setUpcomingInterventions] = useState<any[]>([]);
  const [pastInterventions, setPastInterventions] = useState<any[]>([]);
  
  // Contact Form
  const [requestType, setRequestType] = useState('message_general');
  const [requestContent, setRequestContent] = useState('');
  const [requestInterventionId, setRequestInterventionId] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [formSuccess, setFormSuccess] = useState('');
  const [formError, setFormError] = useState('');

  // Report Modal
  const [selectedReport, setSelectedReport] = useState<any>(null);

  // Chat / Messages
  const [requests, setRequests] = useState<any[]>([]);
  const [activeRequest, setActiveRequest] = useState<any | null>(null);
  const [messageContent, setMessageContent] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  const [settingsFullName, setSettingsFullName] = useState('');
  const [settingsPassword, setSettingsPassword] = useState('');
  const [settingsAddress, setSettingsAddress] = useState('');
  const [settingsPostalCode, setSettingsPostalCode] = useState('');
  const [settingsCity, setSettingsCity] = useState('');
  const [settingsVatNumber, setSettingsVatNumber] = useState('');
  const [settingsSiret, setSettingsSiret] = useState('');
  const [settingsIban, setSettingsIban] = useState('');
  const [settingsMessage, setSettingsMessage] = useState('');
  const [settingsLoading, setSettingsLoading] = useState(false);

  useEffect(() => {
    const checkUser = async () => {
      if (!supabase) {
        navigate('/login');
        return;
      }
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login');
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('role, full_name, billing_details')
        .eq('id', session.user.id)
        .single();

      // Forcer le type car single() retourne any dans certains cas
      const typedProfile = profile as { role: string; full_name: string; billing_details: any } | null;

      if (typedProfile?.role === 'admin') {
        navigate('/admin');
        return;
      }
      
      if (typedProfile?.role === 'collaborateur') {
        navigate('/collaborator');
        return;
      }
      
      if (typedProfile) {
        setSettingsFullName(typedProfile.full_name || '');
        if (typedProfile.billing_details) {
          setSettingsAddress(typedProfile.billing_details.address || '');
          setSettingsPostalCode(typedProfile.billing_details.postalCode || '');
          setSettingsCity(typedProfile.billing_details.city || '');
          setSettingsVatNumber(typedProfile.billing_details.vatNumber || '');
          setSettingsSiret(typedProfile.billing_details.siret || '');
          if (typedProfile.billing_details.iban) {
            setSettingsIban(`•••• •••• •••• ${typedProfile.billing_details.iban.slice(-4)}`);
          }
        }
      }

      setUser(session.user);
      fetchData(session.user.id);
    };
    checkUser();
  }, [navigate]);

  const fetchRequests = async (clientId: string) => {
    if (!supabase) return;
    const { data: reqs } = await supabase
      .from('client_requests')
      .select(`
        *,
        messages(*, sender:profiles(full_name, role))
      `)
      .eq('client_id', clientId)
      .order('created_at', { ascending: false });

    if (reqs) {
      reqs.forEach(r => {
        if (r.messages) r.messages.sort((a:any, b:any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      });
      setRequests(reqs);
      
      if (activeRequest) {
        const updatedReq = reqs.find(r => r.id === activeRequest.id);
        if (updatedReq) setActiveRequest(updatedReq);
      }
    }
  };

  useEffect(() => {
    let interval: any;
    if (user) {
      interval = setInterval(() => {
        fetchRequests(user.id);
      }, 30000);
    }
    return () => clearInterval(interval);
  }, [user, activeRequest]);

  const markAsRead = async (req: any) => {
    if (!supabase || !user) return;
    const unreadMessages = (req.messages || []).filter((m: any) => !m.read_by?.includes(user.id));
    if (unreadMessages.length === 0) return;

    for (const msg of unreadMessages) {
      const updatedReadBy = [...(msg.read_by || []), user.id];
      await supabase.from('messages').update({ read_by: updatedReadBy }).eq('id', msg.id);
    }
    fetchRequests(user.id);
  };

  const unreadCount = requests.reduce((acc, req) => {
    return acc + (req.messages?.filter((m: any) => !m.read_by?.includes(user?.id) && m.sender_id !== user?.id).length || 0);
  }, 0);

  const fetchData = async (clientId: string) => {
    if (!supabase) return;

    await fetchRequests(clientId);

    // Fetch prestations for this client to get prestation IDs
    const { data: prestations } = await supabase
      .from('prestations')
      .select('id')
      .eq('client_id', clientId);

    if (!prestations || prestations.length === 0) {
      setLoading(false);
      return;
    }

    const prestationIds = prestations.map(p => p.id);

    // Fetch interventions
    const { data: interventions } = await supabase
      .from('interventions')
      .select(`
        *,
        prestation:prestations(custom_name, package_tag, type),
        collaborator:profiles(full_name),
        reports(*),
        report_task_logs(*)
      `)
      .in('prestation_id', prestationIds)
      .order('scheduled_at', { ascending: false });

    if (interventions) {
      
      // Séparer les à venir et les passées
      const upcoming = interventions
        .filter(i => i.status === 'a_venir' || i.status === 'en_cours')
        .sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime());
        
      const past = interventions
        .filter(i => i.status === 'termine' || i.status === 'annule');

      setUpcomingInterventions(upcoming);
      setPastInterventions(past);
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    if (supabase) {
      await supabase.auth.signOut();
    }
    navigate('/login');
  };

  const handleSendRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');
    setFormSuccess('');

    try {
      if (!supabase || !user) throw new Error("Erreur d'authentification");
      
      const payload: any = {
        client_id: user.id,
        type: requestType,
        content: requestContent
      };

      if (requestType === 'probleme_intervention' && requestInterventionId) {
        payload.intervention_id = requestInterventionId;
      }

      const { error } = await supabase.from('client_requests').insert(payload);
      if (error) throw error;

      setFormSuccess("Votre demande a bien été envoyée. Notre équipe vous recontactera très vite.");
      setRequestContent('');
      setRequestInterventionId('');
      fetchData(user.id);
    } catch (err: any) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageContent.trim() || !activeRequest || !supabase || !user) return;
    setSendingMessage(true);
    try {
      const { error } = await supabase.from('messages').insert({
        request_id: activeRequest.id,
        sender_id: user.id,
        content: messageContent
      });
      if (error) throw error;
      setMessageContent('');
      fetchData(user.id);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleUpdateSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setSettingsLoading(true);
    setSettingsMessage('');
    try {
      if (!supabase || !user) return;
      
      const { data: currentProfile } = await supabase
        .from('profiles')
        .select('billing_details')
        .eq('id', user.id)
        .single();

      const existingBilling = currentProfile?.billing_details || {};
      const updatedIban = settingsIban.includes('••••') ? existingBilling.iban : settingsIban;

      const newBillingDetails = {
        ...existingBilling,
        address: settingsAddress,
        postalCode: settingsPostalCode,
        city: settingsCity,
        vatNumber: settingsVatNumber,
        siret: settingsSiret,
        iban: updatedIban
      };

      const updatePayload: any = {
        billing_details: newBillingDetails
      };

      if (settingsFullName) {
        updatePayload.full_name = settingsFullName;
      }

      await supabase.from('profiles').update(updatePayload).eq('id', user.id);
      if (settingsPassword) {
        const { error } = await supabase.auth.updateUser({ password: settingsPassword });
        if (error) throw error;
      }
      setSettingsMessage('Profil mis à jour avec succès.');
      setSettingsPassword('');
    } catch (err: any) {
      setSettingsMessage(err.message);
    } finally {
      setSettingsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-gold-200 border-t-gold-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-cream-100 font-sans text-black-deep">
      
      {/* Sidebar */}
      <aside className="w-72 bg-white shadow-2xl flex flex-col fixed h-full z-20">
        <div className="p-8 border-b border-gray-100 flex flex-col items-center">
          <img src="/assets/logo_v2.webp" alt="L'Art du Ménage" className="h-16 w-auto mb-4" />
          <h2 className="font-serif tracking-widest text-lg">ESPACE CLIENT</h2>
        </div>
        
        <nav className="flex-grow py-8 px-4 space-y-2">
          <button onClick={() => setActiveTab('overview')} className={`w-full flex items-center justify-between px-6 py-4 rounded-sm transition-all ${activeTab === 'overview' ? 'bg-black-deep text-white shadow-md' : 'text-gray-500 hover:bg-cream-50 hover:text-black-deep'}`}>
            <span className="font-medium text-sm tracking-wider uppercase">Vue d'ensemble</span>
            {activeTab === 'overview' && <ChevronRight size={16} className="text-gold-500" />}
          </button>
          
          <button onClick={() => setActiveTab('history')} className={`w-full flex items-center justify-between px-6 py-4 rounded-sm transition-all ${activeTab === 'history' ? 'bg-black-deep text-white shadow-md' : 'text-gray-500 hover:bg-cream-50 hover:text-black-deep'}`}>
            <span className="font-medium text-sm tracking-wider uppercase">Historique</span>
            {activeTab === 'history' && <ChevronRight size={16} className="text-gold-500" />}
          </button>
          
          <button onClick={() => setActiveTab('planning')} className={`w-full flex items-center justify-between px-6 py-4 rounded-sm transition-all ${activeTab === 'planning' ? 'bg-black-deep text-white shadow-md' : 'text-gray-500 hover:bg-cream-50 hover:text-black-deep'}`}>
            <span className="font-medium text-sm tracking-wider uppercase">Mon Planning</span>
            {activeTab === 'planning' && <ChevronRight size={16} className="text-gold-500" />}
          </button>
          
          <button onClick={() => setActiveTab('contact')} className={`w-full flex items-center justify-between px-6 py-4 rounded-sm transition-all ${activeTab === 'contact' ? 'bg-black-deep text-white shadow-md' : 'text-gray-500 hover:bg-cream-50 hover:text-black-deep'}`}>
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm tracking-wider uppercase">Contact & Requêtes</span>
              {unreadCount > 0 && (
                <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{unreadCount}</span>
              )}
            </div>
            {activeTab === 'contact' && <ChevronRight size={16} className="text-gold-500" />}
          </button>

          <button onClick={() => setActiveTab('invoices')} className={`w-full flex items-center justify-between px-6 py-4 rounded-sm transition-all ${activeTab === 'invoices' ? 'bg-black-deep text-white shadow-md' : 'text-gray-500 hover:bg-cream-50 hover:text-black-deep'}`}>
            <span className="font-medium text-sm tracking-wider uppercase">Factures</span>
            {activeTab === 'invoices' && <ChevronRight size={16} className="text-gold-500" />}
          </button>

          <button onClick={() => setActiveTab('settings')} className={`w-full flex items-center justify-between px-6 py-4 rounded-sm transition-all ${activeTab === 'settings' ? 'bg-black-deep text-white shadow-md' : 'text-gray-500 hover:bg-cream-50 hover:text-black-deep'}`}>
            <span className="font-medium text-sm tracking-wider uppercase">Paramètres</span>
            {activeTab === 'settings' && <ChevronRight size={16} className="text-gold-500" />}
          </button>
        </nav>

        <div className="p-4 border-t border-gray-100">
          <button 
            onClick={handleLogout}
            className="w-full flex items-center justify-center space-x-2 px-6 py-4 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-sm transition-colors text-sm uppercase tracking-wider font-bold"
          >
            <LogOut size={18} />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-grow ml-72 p-12 overflow-y-auto">
        <FadeIn>
          <header className="mb-12">
            <p className="text-gold-500 text-sm font-bold uppercase tracking-widest mb-2">Bienvenue</p>
            <h1 className="text-4xl font-serif">Mon Domicile</h1>
          </header>
        </FadeIn>

        {/* --- OVERVIEW --- */}
        {activeTab === 'overview' && (
          <FadeInStagger className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Card URSSAF */}
              <FadeInStaggerItem className="col-span-1 lg:col-span-2 bg-gradient-to-br from-black-deep to-gray-900 rounded-sm shadow-xl p-8 text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 p-8 opacity-10">
                  <ShieldCheck size={120} />
                </div>
                <ShieldCheck size={32} className="text-gold-400 mb-6" />
                <h3 className="text-2xl font-serif mb-2">Service à la personne</h3>
                <p className="text-gray-400 font-light mb-8 max-w-md">
                  Toutes vos interventions bénéficient de l'avance immédiate du crédit d'impôt de 50%.
                </p>
                <div className="bg-white/5 border border-white/10 p-6 rounded-sm backdrop-blur-sm inline-block">
                  <p className="text-xs uppercase tracking-widest text-gray-400 mb-1">Reste à charge</p>
                  <p className="text-4xl font-serif text-gold-400">50%</p>
                </div>
              </FadeInStaggerItem>

              {/* Card Prochaine Intervention */}
              <FadeInStaggerItem className="bg-white rounded-sm shadow-xl p-8 border border-gold-200/50 flex flex-col">
                <div className="flex-grow">
                  <p className="text-gold-500 text-xs font-bold uppercase tracking-widest mb-4">Prochain Passage</p>
                  
                  {upcomingInterventions.length > 0 ? (
                    <div>
                      <h3 className="text-2xl font-serif text-black-deep mb-2">
                        {new Date(upcomingInterventions[0].scheduled_at).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                      </h3>
                      <p className="text-gray-500 font-bold flex items-center gap-2 mb-2">
                        <Clock size={16}/> 
                        {new Date(upcomingInterventions[0].scheduled_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} 
                        ({upcomingInterventions[0].duration_hours}h)
                      </p>
                      <p className="text-sm text-gray-400">
                        {upcomingInterventions[0].prestation?.custom_name}
                      </p>
                    </div>
                  ) : (
                    <div>
                      <h3 className="text-xl font-serif text-black-deep mb-2">Aucun passage prévu</h3>
                      <p className="text-gray-500 font-light text-sm">Votre planning est actuellement vide.</p>
                    </div>
                  )}
                </div>
                <button onClick={() => setActiveTab('contact')} className="w-full mt-8 bg-cream-50 text-black-deep border border-gray-200 py-3 text-sm uppercase tracking-widest font-bold hover:bg-gold-50 hover:text-gold-600 transition-colors">
                  Demande Spéciale
                </button>
              </FadeInStaggerItem>
            </div>
          </FadeInStagger>
        )}

        {/* --- PLANNING --- */}
        {activeTab === 'planning' && (
          <FadeIn>
            <div className="mb-8 flex justify-between items-end">
              <div>
                <h2 className="text-2xl font-serif text-black-deep mb-2">Mon Planning</h2>
                <p className="text-gray-500">Visualisez vos prochaines interventions et repérez facilement les horaires.</p>
              </div>
            </div>
            <ClientCalendar interventions={[...upcomingInterventions, ...pastInterventions]} />
          </FadeIn>
        )}

        {/* --- HISTORY --- */}
        {activeTab === 'history' && (
          <FadeIn>
            <div className="bg-white shadow-xl border border-gray-100 rounded-sm overflow-hidden">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                <h2 className="font-serif text-xl">Historique des interventions</h2>
              </div>
              <div className="p-6 space-y-6">
                {pastInterventions.length === 0 ? (
                  <p className="text-gray-400 italic">Aucune intervention passée trouvée.</p>
                ) : (
                  pastInterventions.map(i => {
                    const date = new Date(i.scheduled_at);
                    const hasReport = i.reports && i.reports.length > 0;
                    const report = hasReport ? i.reports[0] : null;

                    return (
                      <div key={i.id} className="border border-gray-100 p-6 rounded-sm hover:border-gold-300 transition-colors">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <span className="text-xs font-bold text-gray-500 bg-gray-100 px-3 py-1 rounded-sm uppercase tracking-widest mb-3 inline-block">
                              {date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                            </span>
                            <h3 className="text-lg font-serif">{i.prestation?.custom_name}</h3>
                            <p className="text-sm text-gray-400 flex items-center gap-2 mt-1">
                              <Clock size={14}/> {i.duration_hours} Heures • Par {i.collaborator?.full_name || 'notre équipe'}
                            </p>
                          </div>
                          <div>
                             <span className={`px-3 py-1 text-[10px] uppercase tracking-wider font-bold rounded-full ${i.status === 'termine' ? 'bg-gray-100 text-gray-700' : 'bg-red-100 text-red-700'}`}>
                               {i.status === 'termine' ? 'Terminé' : 'Annulé'}
                             </span>
                          </div>
                        </div>

                        {/* Rapport de Transparence Ancien format */}
                        {hasReport && (
                          <div className="bg-cream-50 p-4 rounded-sm border border-gold-100 mt-4">
                            <h4 className="text-xs uppercase tracking-widest font-bold text-gold-600 mb-3 flex items-center gap-2">
                              <ShieldCheck size={14} /> Suivi Qualité (Transparence)
                            </h4>
                            
                            {report.general_notes && (
                              <p className="text-sm text-gray-600 mb-2 italic">"{report.general_notes}"</p>
                            )}

                            {report.special_tasks && (
                              <p className="text-sm text-gray-700 mt-2 flex items-center gap-2">
                                <Sparkles size={14} className="text-gold-500"/>
                                <strong>Actions Spéciales :</strong> {report.special_tasks}
                              </p>
                            )}

                            {report.problems_encountered && (
                              <p className="text-sm text-red-700 mt-2 flex items-start gap-2 bg-red-50 p-2 rounded">
                                <AlertTriangle size={14} className="mt-0.5 flex-shrink-0"/>
                                <span><strong>Signalement équipe :</strong> {report.problems_encountered}</span>
                              </p>
                            )}

                            {report.photos && report.photos.length > 0 && (
                              <button 
                                onClick={() => setSelectedReport(report)}
                                className="mt-4 text-xs font-bold uppercase tracking-widest flex items-center gap-2 text-gold-600 hover:text-gold-500"
                              >
                                <Camera size={14} /> Voir les {report.photos.length} photos du passage
                              </button>
                            )}
                          </div>
                        )}

                        {/* Rapport Premium (Nouveau format) */}
                        {i.report_task_logs && i.report_task_logs.length > 0 && (
                          <div className="bg-white p-6 rounded-sm border border-gold-200 mt-6 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="text-sm uppercase tracking-widest font-bold text-black-deep flex items-center gap-2">
                                <ShieldCheck size={18} className="text-gold-500" /> Rapport d'Intervention Détaillé
                              </h4>
                              <span className="text-xs font-bold text-gold-600 bg-gold-50 px-3 py-1 rounded-sm">
                                {Math.round((i.report_task_logs.filter((l:any) => l.status === 'fait').length / i.report_task_logs.length) * 100)}% Complété
                              </span>
                            </div>

                            <div className="space-y-4">
                              {Array.from(new Set(i.report_task_logs.map((l:any) => l.category))).map(cat => (
                                <div key={cat as string} className="border border-gray-100 rounded-sm overflow-hidden">
                                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
                                    <h5 className="text-xs font-bold uppercase tracking-widest text-gray-700">{cat as string}</h5>
                                  </div>
                                  <div className="divide-y divide-gray-100">
                                    {i.report_task_logs.filter((l:any) => l.category === cat).map((log:any) => (
                                      <div key={log.id} className="p-3 bg-white">
                                        <div className="flex justify-between items-start">
                                          <div className="flex items-center gap-2">
                                            {log.status === 'fait' ? <Check size={16} className="text-green-500 flex-shrink-0" /> : 
                                             log.status === 'probleme' ? <AlertTriangle size={16} className="text-red-500 flex-shrink-0" /> : 
                                             <X size={16} className="text-gray-300 flex-shrink-0" />}
                                            <span className={`text-sm ${log.status === 'fait' ? 'text-black-deep' : 'text-gray-500'}`}>{log.name}</span>
                                          </div>
                                        </div>
                                        {(log.comment || log.photo_url) && (
                                          <div className="mt-2 ml-6 pl-3 border-l-2 border-gold-200 space-y-2">
                                            {log.comment && <p className="text-xs text-gray-600 italic">"{log.comment}"</p>}
                                            {log.photo_url && (
                                              <a href={log.photo_url} target="_blank" rel="noreferrer" className="text-xs text-gold-600 hover:text-gold-700 font-bold flex items-center gap-1">
                                                <Camera size={12} /> Voir la photo
                                              </a>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Bouton signaler un souci */}
                        <div className="mt-4 pt-4 border-t border-gray-100 flex justify-end">
                          <button onClick={() => {
                            setRequestInterventionId(i.id);
                            setRequestType('probleme_intervention');
                            setActiveTab('contact');
                          }} className="text-xs font-bold text-gray-400 hover:text-red-500 uppercase tracking-widest transition-colors flex items-center gap-2">
                            <AlertTriangle size={14}/> Un souci avec cette intervention ?
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </FadeIn>
        )}

        {/* --- CONTACT & MESSAGERIE --- */}
        {activeTab === 'contact' && (
          <FadeIn>
            <div className="bg-white shadow-xl border border-gray-100 rounded-sm overflow-hidden flex" style={{ height: '70vh' }}>
              {/* Left Sidebar: List of requests */}
              <div className="w-1/3 border-r border-gray-100 flex flex-col bg-gray-50">
                <div className="p-4 border-b border-gray-100 bg-white flex justify-between items-center">
                  <h3 className="font-serif text-lg">Conversations</h3>
                  <button onClick={() => setActiveRequest(null)} className="text-xs uppercase tracking-widest font-bold text-gold-600 hover:text-gold-500 bg-gold-50 px-3 py-1 rounded">
                    Nouveau
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {requests.length === 0 ? (
                    <p className="p-4 text-sm text-gray-400 italic text-center mt-4">Aucune conversation.</p>
                  ) : (
                    requests.map(req => (
                      <button 
                        key={req.id} 
                        onClick={() => { setActiveRequest(req); markAsRead(req); }}
                        className={`w-full text-left p-4 border-b border-gray-100 hover:bg-gray-100 transition-colors ${activeRequest?.id === req.id ? 'bg-white border-l-4 border-l-gold-500' : ''}`}
                      >
                        <div className="flex justify-between items-start mb-1">
                          <span className="text-xs font-bold uppercase text-gray-500">
                            {req.type === 'message_general' ? 'Général' : req.type === 'demande_speciale' ? 'Spécial' : 'Problème'}
                          </span>
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${req.status === 'traite' ? 'bg-green-100 text-green-700' : 'bg-gold-100 text-gold-700'}`}>
                            {req.status === 'traite' ? 'Traité' : 'En cours'}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-black-deep line-clamp-1">{req.content}</p>
                        <p className="text-xs text-gray-400 mt-2">{new Date(req.created_at).toLocaleDateString('fr-FR')}</p>
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Right Pane: Active Chat or New Request Form */}
              <div className="w-2/3 flex flex-col bg-white">
                {!activeRequest ? (
                  <div className="p-8 flex-1 overflow-y-auto">
                    <h2 className="font-serif text-2xl mb-2">Nouvelle demande</h2>
                    <p className="text-gray-500 mb-8 font-light text-sm">Une demande spéciale, un réajustement ou une question ? Nous vous répondons dans les plus brefs délais.</p>

                    {formError && <div className="mb-6 p-4 bg-red-50 text-red-700 border border-red-200 text-sm rounded">{formError}</div>}
                    {formSuccess && <div className="mb-6 p-4 bg-green-50 text-green-700 border border-green-200 text-sm rounded flex items-center gap-2"><Check size={18} /> {formSuccess}</div>}

                    <form onSubmit={handleSendRequest} className="space-y-6 max-w-xl">
                      <div>
                        <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2 font-bold">Nature de la demande</label>
                        <select value={requestType} onChange={e => setRequestType(e.target.value)} className="w-full border border-gray-300 p-3 focus:outline-none focus:border-gold-500 rounded-sm bg-gray-50 text-sm">
                          <option value="message_general">Question Générale</option>
                          <option value="demande_speciale">Demander une prestation exceptionnelle</option>
                          <option value="probleme_intervention">Signaler un problème sur une intervention passée</option>
                        </select>
                      </div>

                      {requestType === 'probleme_intervention' && (
                        <div>
                          <label className="block text-xs uppercase tracking-widest text-red-500 mb-2 font-bold">Intervention concernée</label>
                          <select required value={requestInterventionId} onChange={e => setRequestInterventionId(e.target.value)} className="w-full border border-red-200 p-3 focus:outline-none focus:border-red-500 rounded-sm bg-red-50 text-sm">
                            <option value="" disabled>-- Sélectionnez l'intervention --</option>
                            {pastInterventions.map(i => (
                              <option key={i.id} value={i.id}>
                                {new Date(i.scheduled_at).toLocaleDateString('fr-FR')} - {i.prestation?.custom_name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      <div>
                        <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2 font-bold">Premier message</label>
                        <textarea required rows={4} value={requestContent} onChange={e => setRequestContent(e.target.value)} className="w-full border border-gray-300 p-3 focus:outline-none focus:border-gold-500 rounded-sm text-sm" placeholder="Détaillez votre demande..."></textarea>
                      </div>

                      <div className="pt-4 flex justify-end">
                        <button type="submit" disabled={formLoading} className="px-6 py-3 bg-black-deep text-white text-xs uppercase tracking-widest font-bold hover:bg-gold-600 transition-colors disabled:opacity-50 flex items-center gap-2 shadow-lg">
                          {formLoading && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>}
                          Ouvrir la conversation
                        </button>
                      </div>
                    </form>
                  </div>
                ) : (
                  <div className="flex flex-col h-full">
                    {/* Chat Header */}
                    <div className="p-4 border-b border-gray-100 bg-white">
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="font-serif text-lg">
                            {activeRequest.type === 'message_general' ? 'Question Générale' : activeRequest.type === 'demande_speciale' ? 'Demande Spéciale' : 'Problème Intervention'}
                          </h3>
                          <p className="text-xs text-gray-500">Ouvert le {new Date(activeRequest.created_at).toLocaleDateString('fr-FR')}</p>
                        </div>
                        <span className={`text-[10px] font-bold uppercase px-3 py-1 rounded-full ${activeRequest.status === 'traite' ? 'bg-green-100 text-green-700' : 'bg-gold-100 text-gold-700'}`}>
                          {activeRequest.status === 'traite' ? 'Traité' : 'En cours'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Messages List */}
                    <div className="flex-1 p-6 overflow-y-auto bg-gray-50 space-y-6">
                      {/* Initial Request Message */}
                      <div className="flex flex-col items-end">
                        <div className="bg-black-deep text-white p-4 rounded-lg rounded-tr-none max-w-[80%] shadow-sm">
                          <p className="text-sm whitespace-pre-wrap">{activeRequest.content}</p>
                        </div>
                        <span className="text-[10px] text-gray-400 mt-1">Vous • {new Date(activeRequest.created_at).toLocaleTimeString('fr-FR', {hour: '2-digit', minute:'2-digit'})}</span>
                      </div>

                      {/* Replies */}
                      {activeRequest.messages?.map((msg: any) => {
                        const isMe = msg.sender_id === user.id;
                        return (
                          <div key={msg.id} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                            <div className={`p-4 rounded-lg shadow-sm max-w-[80%] ${isMe ? 'bg-black-deep text-white rounded-tr-none' : 'bg-white border border-gray-100 rounded-tl-none'}`}>
                              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            </div>
                            <span className="text-[10px] text-gray-400 mt-1">
                              {isMe ? 'Vous' : msg.sender?.full_name} • {new Date(msg.created_at).toLocaleTimeString('fr-FR', {hour: '2-digit', minute:'2-digit'})}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {/* Chat Input */}
                    {activeRequest.status !== 'traite' && (
                      <div className="p-4 bg-white border-t border-gray-100">
                        <form onSubmit={handleSendMessage} className="flex gap-2">
                          <textarea 
                            rows={1}
                            value={messageContent}
                            onChange={e => setMessageContent(e.target.value)}
                            placeholder="Votre réponse..."
                            className="flex-1 border border-gray-300 p-3 text-sm focus:outline-none focus:border-gold-500 rounded-sm resize-none"
                            onKeyDown={e => {
                              if(e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSendMessage(e as any);
                              }
                            }}
                          ></textarea>
                          <button 
                            type="submit" 
                            disabled={sendingMessage || !messageContent.trim()}
                            className="px-6 bg-gold-600 text-white text-xs font-bold uppercase tracking-widest hover:bg-gold-500 transition-colors rounded-sm disabled:opacity-50"
                          >
                            {sendingMessage ? '...' : 'Envoyer'}
                          </button>
                        </form>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </FadeIn>
        )}

        {/* --- INVOICES (PLACEHOLDER) --- */}
        {activeTab === 'invoices' && (
          <FadeIn>
            <div className="bg-white shadow-xl border border-gray-100 rounded-sm p-8 text-center">
              <FileText size={48} className="text-gray-200 mx-auto mb-4" />
              <h2 className="font-serif text-2xl mb-2">Vos factures</h2>
              <p className="text-gray-500 font-light max-w-md mx-auto">
                Vos factures apparaîtront ici à la fin de chaque cycle de facturation. Elles intégreront automatiquement les informations nécessaires à l'avance immédiate du crédit d'impôt.
              </p>
            </div>
          </FadeIn>
        )}

        {activeTab === 'settings' && (
          <FadeIn>
            <form onSubmit={handleUpdateSettings} className="bg-white shadow-xl border border-gray-100 rounded-sm p-8 max-w-xl">
              <h2 className="font-serif text-xl mb-6 border-b border-gray-100 pb-4">Mes Informations</h2>
              
              {settingsMessage && <div className={`mb-6 p-4 text-sm rounded flex items-center gap-2 ${settingsMessage.includes('succès') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                {settingsMessage}
              </div>}

              <div className="space-y-6">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2 font-bold">Nom Complet</label>
                  <input type="text" required value={settingsFullName} onChange={e => setSettingsFullName(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500 bg-transparent" />
                </div>
                
                <h3 className="font-serif text-lg mt-8 pt-6 border-t border-gray-100 mb-4">Coordonnées & Facturation</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2 font-bold">Adresse postale</label>
                    <input type="text" value={settingsAddress} onChange={e => setSettingsAddress(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500 bg-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2 font-bold">Code Postal</label>
                    <input type="text" value={settingsPostalCode} onChange={e => setSettingsPostalCode(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500 bg-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2 font-bold">Ville</label>
                    <input type="text" value={settingsCity} onChange={e => setSettingsCity(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500 bg-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2 font-bold">Numéro de TVA</label>
                    <input type="text" value={settingsVatNumber} onChange={e => setSettingsVatNumber(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500 bg-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2 font-bold">SIRET / SIREN</label>
                    <input type="text" value={settingsSiret} onChange={e => setSettingsSiret(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500 bg-transparent" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2 font-bold flex items-center gap-2">IBAN <ShieldCheck size={14} className="text-green-600"/></label>
                    <input 
                      type="text" 
                      value={settingsIban} 
                      onChange={e => setSettingsIban(e.target.value)} 
                      placeholder="Saisissez votre IBAN"
                      className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500 bg-transparent font-mono text-sm" 
                    />
                    <p className="text-[10px] text-gray-400 mt-1">Vos coordonnées bancaires sont chiffrées et stockées de manière sécurisée (conformité RGPD).</p>
                  </div>
                </div>

                <h3 className="font-serif text-lg mt-8 pt-6 border-t border-gray-100 mb-4">Sécurité</h3>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2 font-bold">Nouveau Mot de passe (optionnel)</label>
                  <input type="password" value={settingsPassword} onChange={e => setSettingsPassword(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500 text-sm bg-transparent" placeholder="Laisser vide pour ne pas changer" />
                </div>
              </div>

              <div className="pt-8 flex justify-end">
                <button type="submit" disabled={settingsLoading} className="px-8 py-4 bg-black-deep text-white text-sm uppercase tracking-widest font-bold hover:bg-gold-600 transition-colors disabled:opacity-50 flex items-center gap-2 shadow-lg">
                  {settingsLoading && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>}
                  Mettre à jour
                </button>
              </div>
            </form>
          </FadeIn>
        )}

      </main>

      {/* Modal pour voir les photos d'un rapport */}
      {selectedReport && (
        <div className="fixed inset-0 bg-black-deep/90 z-50 overflow-y-auto p-6 flex items-center justify-center backdrop-blur-sm">
          <div className="w-full max-w-4xl relative">
            <button onClick={() => setSelectedReport(null)} className="absolute -top-12 right-0 text-white hover:text-gold-500">
              <X size={32} />
            </button>
            <h3 className="text-white font-serif text-2xl mb-6">Photos de l'intervention</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {selectedReport.photos.map((url: string, idx: number) => (
                <a key={idx} href={url} target="_blank" rel="noreferrer" className="block rounded-sm overflow-hidden border border-white/20 shadow-2xl">
                  <img src={url} alt={`Photo ${idx + 1}`} className="w-full h-auto object-cover hover:scale-105 transition-transform duration-500" />
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
